import { isNotEmptyTrimmedStringValidator } from '@currency-one/validators'
import { promiseWrapper } from 'utils/promise'
import { jsEscape } from 'utils/prevent-xss-attack'
import { getCurrentLanguage } from 'src/assets/scripts/utils/current-language'

type FormContainer = Document | HTMLElement
type StringValidator = (value: string, selector: string, formContainer: FormContainer) => string
type ContentJson = {
  title: string
  content: string
  url: string
}

declare let CMS_BASE_URL_WT: string

const $lang: string = getCurrentLanguage().toUpperCase()
const ERROR_MSGS = {
  PL: {
    SEARCHPHASE: {
      EMPTY: 'Nie wypełniono pola wyszukiwarki.',
      NOMATCH: 'Nie znaleziono artykułów zawierających frazę "<<phase>>".',
      PREVENTXSS: 'Nie znaleziono artykułów.',
    },
    SERVERISSUES: 'Wystąpił błąd, spróbuj później.',
  },
  EN: {
    SEARCHPHASE: {
      EMPTY: 'The search box has not been filled in.',
      NOMATCH: 'No articles containing the phrase were found "<<phase>>".',
      PREVENTXSS: 'No articles found.',
    },
    SERVERISSUES: 'An error occurred, please try again later.',
  },
}

/**
 * Function set error MSG for span and highlight invalid form field.
 *
 * @param {string} selector
 * @param {string} errorMSG
 * @param {FormContainer} formContainer
 */
const setErrorMSG = (selector: string, errorMSG: string, formContainer: FormContainer): void => {
  const $inputWithError = formContainer.querySelector<HTMLInputElement>(selector)

  let $inputParentWithError = $inputWithError as HTMLElement
  let $errorSpanElement: HTMLSpanElement | null

  // Lets traverse DOM and search for parent that contain <span> marked as label for error MSG
  do {
    $inputParentWithError = <HTMLElement>$inputParentWithError.parentNode
    $errorSpanElement = $inputParentWithError.querySelector<HTMLSpanElement>('[data-error-field]')
  } while ($errorSpanElement === null)

  if ($errorSpanElement) {
    $errorSpanElement.innerHTML = errorMSG
    $errorSpanElement.hidden = false
  }

  if ($inputWithError) {
    $inputWithError.classList.add('is-invalid')
  }
}

/**
 * Function checks if array of test is empty. If not set error label based on detected error
 * and add is-invalid class for input.
 *
 * @param {FormContainer} formContainer
 * @param {Array<string>} checkResults
 * @param {string} selector
 * @return {*}  {boolean}
 */
const checkValidity = (checkResults: Array<string>, selector: string, formContainer: FormContainer): boolean => {
  checkResults = checkResults.filter((n) => n)
  if (checkResults.length) {
    setErrorMSG(selector, checkResults[0], formContainer)
    return false
  }
  return true
}

/**
 * Validator for text input field.
 *
 * @param {string} value
 * @param {string} selector
 * @param {FormContainer} formContainer
 * @return {*}  {string}
 */
const stringValidator = (value: string, selector: string, formContainer: FormContainer): string => {
  let isValid = [!isNotEmptyTrimmedStringValidator(value) ? ERROR_MSGS[$lang].SEARCHPHASE.EMPTY : '']
  isValid = [value.includes('\\u') ? ERROR_MSGS[$lang].SEARCHPHASE.PREVENTXSS : '']
  return checkValidity(isValid, selector, formContainer) ? value : ''
}

/**
 * Function retrieves and validate the value of an input field.
 *
 * @param {string} selector
 * @param {StringValidator} validator
 * @param {FormContainer} formContainer
 * @return {*}  {string}
 */
const getStringField = (selector: string, validator: StringValidator, formContainer: FormContainer): string => {
  const ALLOWED_INPUT_TYPES = ['text']
  const FALLBACK_RETURN_VALUE = ''
  const $inputElement = formContainer.querySelector<HTMLInputElement>(selector)
  if ($inputElement) {
    return ALLOWED_INPUT_TYPES.includes($inputElement.type)
      ? validator(String(jsEscape($inputElement.value)).trim(), selector, formContainer)
      : FALLBACK_RETURN_VALUE
  }
  return FALLBACK_RETURN_VALUE
}

/**
 * Function reset all error spans for form and remove is-invalid class from inputs.
 *
 * @param {FormContainer} formContainer
 */
const resetAllErrorMSG = (formContainer: FormContainer): void => {
  const $errorMsgsSpans = formContainer.querySelectorAll<HTMLSpanElement>('[data-error-field]')
  const $invalidInputs = formContainer.querySelectorAll<HTMLInputElement>('.is-invalid')
  $errorMsgsSpans.forEach(($singleErrorSpan) => {
    $singleErrorSpan.hidden = true
  })

  $invalidInputs.forEach(($singleInvalidField) => {
    $singleInvalidField.classList.remove('is-invalid')
  })
}

/**
 * Get help.json resource.
 *
 * @return {*}  {Promise<ContentJson[]>}
 */
const getJsonUrl: string = `${CMS_BASE_URL_WT}/wp-json/help/v2/posts/${$lang.toLowerCase()}`
const getJsonContent = (): Promise<ContentJson[]> => {
  return promiseWrapper<ContentJson[], null>(getJsonUrl, 'GET')
}

/**
 * Perform search in titles add content for given searchPhase.
 *
 * @param {ContentJson[]} dataCollection
 * @param {string} searchPhase
 * @return {*}  {ContentJson[]}
 */
const searchInCollection = (dataCollection: ContentJson[], searchPhase: string): ContentJson[] => {
  searchPhase = searchPhase.toLowerCase()
  const searchInTitles = dataCollection.filter((record) => record.title.toLowerCase().includes(searchPhase))
  const searchInContents = dataCollection.filter((record) => record.content.toLowerCase().includes(searchPhase))
  return [...new Set([...searchInTitles, ...searchInContents])]
}

/**
 * Cut string to length. Function trim string to last spacebar.
 *
 * @param {string} string
 * @param {number} maxStringLength
 * @return {*}  {string}
 */
const cutString = (string: string, maxStringLength: number): string => {
  const cut = string.indexOf(' ', maxStringLength)
  if (cut == -1) return string
  return string.substring(0, cut)
}

/**
 * Change state of hidden attribute of element
 *
 * @param {HTMLElement} $element
 * @param {boolean} state
 */
const changeElementHiddenState = ($element: HTMLElement, state: boolean): void => {
  if ($element) {
    $element.hidden = state
  }
}

/**
 * Update node text content.
 *
 * @param {HTMLElement} $element
 * @param {string} searchPhase
 */
const updateViewSpans = ($element: HTMLElement, searchPhase: string): void => {
  if ($element) {
    $element.innerHTML = searchPhase
  }
}

/**
 * Update and show result view.
 *
 * @param {ContentJson[]} searchResults
 * @param {string} searchPhase
 */
const showResultScreen = (searchResults: ContentJson[], searchPhase: string): void => {
  const $resultContainer = document.querySelector<HTMLDivElement>('[data-search-result-container]')

  // Remove child elements - past search results
  while ($resultContainer.lastElementChild) {
    $resultContainer.removeChild($resultContainer.lastElementChild)
  }

  // clone from template document-fragment and update content to add to DOM
  searchResults.forEach((singleResult) => {
    const $newElement = document.createElement('div')
    $newElement.classList.add('help-search-results__single-result')
    $newElement.insertAdjacentHTML(
      'afterbegin',
      `<div class='help-search-results__single-result-title'><a href='${encodeURI(
        singleResult.url,
      )}' class='help-search-results__single-result-title-anchor'>${singleResult.title}</a>
  </div><div class='help-search-results__single-result-content'>${cutString(singleResult.content, 180)}</div>`,
    )
    $resultContainer.appendChild($newElement)
  })

  const $showSearchPhaseElement = document.querySelector<HTMLSpanElement>('[data-search-phase]')
  updateViewSpans($showSearchPhaseElement, searchResults.length.toString())

  const $showResultsNumber = document.querySelector<HTMLSpanElement>('[data-search-result-amount]')
  updateViewSpans($showResultsNumber, searchPhase)

  const $resultsElement = document.querySelector<HTMLDivElement>('[data-help-results-view]')
  changeElementHiddenState($resultsElement, false)

  const $helpSectionElement = document.querySelector<HTMLDivElement>('[data-help-section]')
  changeElementHiddenState($helpSectionElement, true)
}

/**
 * Handle search button action.
 *
 * @param {FormContainer} formContainer
 */
const handleSearchButton = (formContainer: FormContainer): void => {
  resetAllErrorMSG(formContainer)
  const searchPhase = getStringField('[data-help-search-input]', stringValidator, formContainer)
  // eslint-disable-next-line no-extra-boolean-cast
  if (!!searchPhase) {
    getJsonContent()
      .then((dataCollection: ContentJson[]): void => {
        const searchResults = searchInCollection(dataCollection, searchPhase)
        if (searchResults.length) {
          showResultScreen(searchResults, searchPhase)
        } else {
          setErrorMSG(
            '[data-help-search-input]',
            ERROR_MSGS[$lang].SEARCHPHASE.NOMATCH.replace('<<phase>>', searchPhase),
            formContainer,
          )

          const $resultsElement = document.querySelector<HTMLDivElement>('[data-help-results-view]')
          changeElementHiddenState($resultsElement, true)

          const $helpSectionElement = document.querySelector<HTMLDivElement>('[data-help-section]')
          changeElementHiddenState($helpSectionElement, false)
        }
      })
      .catch((error) => {
        console.error(error)
        setErrorMSG('[data-help-search-input]', ERROR_MSGS[$lang].SERVERISSUES, formContainer)
      })
  } else {
    setErrorMSG('[data-help-search-input]', ERROR_MSGS[$lang].SEARCHPHASE.EMPTY, formContainer)
  }
}

export const initSearchForm = (): void => {
  const $form = document.querySelector<HTMLFormElement>('[data-search-form]')
  if ($form) {
    const $searchButton = document.querySelector<HTMLButtonElement>('[data-help-search-button]')
    $searchButton.addEventListener('click', () => handleSearchButton($form))
    $searchButton.disabled = false
    const $searchInput = document.querySelector<HTMLButtonElement>('[data-help-search-input]')
    $searchInput.disabled = false

    $searchInput.addEventListener('keypress', function (event) {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') handleSearchButton($form)
    })
  }
}
